import { useTranslation } from "react-i18next";
import { usePageSEO } from "@/lib/use-page-seo";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MegaFooter from "@/components/MegaFooter";
import { WC2026_TEAMS } from "../../../server/teams";
import { slugifyTeam, teamPath } from "../../../shared/seo-routes";

const CONFEDERATIONS = ["UEFA", "CONMEBOL", "CONCACAF", "AFC", "CAF", "OFC"] as const;

export default function TeamsIndex() {
  usePageSEO("seo.team.index_title", "/teams");
  const { t } = useTranslation();
  const lp = useLocalePath();

  type TeamRecord = (typeof WC2026_TEAMS)[number];
  const byConfederation: Record<string, TeamRecord[]> = {};
  for (const conf of CONFEDERATIONS) byConfederation[conf] = [];
  for (const team of WC2026_TEAMS) {
    if (team.confederation in byConfederation) {
      byConfederation[team.confederation].push(team);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground">
              {t("nav.back")}
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
          <a href={lp("/")} className="hover:underline">{t("seo.common.home")}</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{t("seo.team.index_h1")}</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {t("seo.team.index_h1")}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-10">
          {t("seo.team.index_intro")}
        </p>

        {CONFEDERATIONS.map((conf) => (
          <section key={conf} className="mb-10">
            <h2 className="text-xl font-bold uppercase mb-4 tracking-wide"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {conf}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {byConfederation[conf].map((team) => (
                <a key={team.code}
                   href={lp(teamPath(slugifyTeam(team.name)))}
                   className="flex items-center gap-3 rounded-md border border-border/50 px-3 py-2.5 hover:bg-muted/30 hover:border-border transition-colors">
                  <span className="text-2xl" aria-hidden="true">{team.flag}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{team.name}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {t("seo.team.group_label", { group: team.group })}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-12 flex flex-wrap gap-3">
          <a href={lp("/")}
             className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:opacity-90">
            {t("seo.common.cta_pick_teams")}
          </a>
          <a href={lp("/groups")}
             className="inline-flex items-center px-5 py-2.5 rounded-md border border-border text-sm hover:bg-muted/30">
            {t("seo.group.index_h1")}
          </a>
        </div>
      </main>

      <MegaFooter />
    </div>
  );
}
